import React from 'react';
import { Bus, Clock } from 'lucide-react';
import { colors, borderRadius } from '../../styles/tokens';
import { useBlockEditor } from '../../hooks/useBlockEditor';
import { EditableField } from '../editing';

const SEAT_CLASS_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'leito', label: '🛏️ Leito' },
  { value: 'semi-leito', label: '💺 Semi-leito' },
  { value: 'convencional', label: '💺 Convencional' },
  { value: 'executivo', label: '✨ Executivo' },
];

export default function BusCard({ block, onRemove, quoteId, onBlockUpdate }) {
  const editable = !!quoteId;
  const { saving, updateField, updateDetail, isSaved } =
    useBlockEditor(quoteId, block, onBlockUpdate);

  const details = block.details || {};

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const seatClassLabel = (cls) => {
    if (!cls) return '-';
    const opt = SEAT_CLASS_OPTIONS.find(o => o.value === cls.toLowerCase());
    return opt ? opt.label : cls;
  };

  const E = (value, onSave, opts = {}) => {
    if (!editable) return opts.fallback || value || opts.placeholder || '-';
    return (
      <EditableField
        value={value}
        onSave={onSave}
        type={opts.type || 'text'}
        options={opts.options}
        saving={saving}
        saved={opts.saved}
        placeholder={opts.placeholder || '-'}
        style={opts.style}
        validate={opts.validate}
      />
    );
  };

  return (
    <div style={{
      backgroundColor: colors.card, border: `1px solid ${colors.border}`,
      borderRadius: borderRadius.md, padding: '1.5rem', position: 'relative',
    }}>
      <button onClick={() => onRemove(block.id)} style={{
        position: 'absolute', top: '1rem', right: '1rem',
        background: 'none', border: 'none', cursor: 'pointer',
        color: colors.text.muted, fontSize: '1.25rem',
      }}>×</button>

      <div style={{ display: 'flex', gap: '1rem' }}>
        <div style={{
          width: '48px', height: '48px', backgroundColor: `${colors.primary}10`,
          borderRadius: borderRadius.sm, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          <Bus size={24} color={colors.primary} />
        </div>

        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: '1.125rem', fontWeight: 600, color: colors.primary, margin: '0 0 1rem 0' }}>
            {E(block.title, (v) => updateField('title', v), { saved: isSaved('title'), fallback: block.title })}
          </h4>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {/* Viação */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Viação</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.company, (v) => updateDetail('company', v), {
                  saved: isSaved('company'), fallback: details.company || '-',
                })}
              </p>
            </div>

            {/* Data */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Data da viagem</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(block.date, (v) => updateField('date', v), {
                  type: 'date', saved: isSaved('date'), fallback: formatDate(block.date),
                })}
              </p>
            </div>

            {/* Partida */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Partida</p>
              <p style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
                {E(details.departure_time, (v) => updateDetail('departure_time', v), {
                  type: 'time', saved: isSaved('departure_time'), fallback: details.departure_time || '--:--',
                })}
                <span style={{ fontWeight: 400, fontSize: '0.875rem' }}>
                  {details.departure_city && (
                    <>
                      {' - '}
                      {E(details.departure_city, (v) => updateDetail('departure_city', v), {
                        saved: isSaved('departure_city'), style: { fontWeight: 400 }, fallback: details.departure_city,
                      })}
                    </>
                  )}
                </span>
              </p>
            </div>

            {/* Chegada */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Chegada</p>
              <p style={{ fontSize: '1rem', fontWeight: 700, color: colors.text.primary, margin: 0 }}>
                {E(details.arrival_time, (v) => updateDetail('arrival_time', v), {
                  type: 'time', saved: isSaved('arrival_time'), fallback: details.arrival_time || '--:--',
                })}
                <span style={{ fontWeight: 400, fontSize: '0.875rem' }}>
                  {details.arrival_city && (
                    <>
                      {' - '}
                      {E(details.arrival_city, (v) => updateDetail('arrival_city', v), {
                        saved: isSaved('arrival_city'), style: { fontWeight: 400 }, fallback: details.arrival_city,
                      })}
                    </>
                  )}
                </span>
              </p>
            </div>

            {/* Classe */}
            <div>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Classe</p>
              <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                {E(details.seat_class || '', (v) => updateDetail('seat_class', v), {
                  type: 'select', options: SEAT_CLASS_OPTIONS,
                  saved: isSaved('seat_class'), fallback: seatClassLabel(details.seat_class),
                })}
              </p>
            </div>

            {/* Duração */}
            {(details.duration || editable) && (
              <div>
                <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Duração</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Clock size={14} color={colors.text.secondary} />
                  <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                    {E(details.duration, (v) => updateDetail('duration', v), {
                      saved: isSaved('duration'), fallback: details.duration || '-', placeholder: 'ex: 6h30',
                    })}
                  </p>
                </div>
              </div>
            )}

            {/* Plataforma */}
            {details.platform && (
              <div>
                <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Plataforma</p>
                <p style={{ fontSize: '0.875rem', fontWeight: 600, color: colors.text.primary, margin: 0 }}>
                  {E(details.platform, (v) => updateDetail('platform', v), {
                    saved: isSaved('platform'), fallback: details.platform,
                  })}
                </p>
              </div>
            )}

            {/* Valor */}
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '0.75rem', color: colors.text.muted, marginBottom: '0.25rem' }}>Valor</p>
              <p style={{ fontSize: '1.25rem', fontWeight: 700, color: colors.accent, margin: 0 }}>
                {E(block.price || 0, (v) => updateField('price', v), {
                  type: 'currency', saved: isSaved('price'),
                  validate: (v) => v < 0 ? 'Preço inválido' : null,
                  fallback: `R$ ${(block.price || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`,
                })}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
